import { useEffect, useRef, useState } from 'react';
import '../scrollfx.css';

// Rotating verb in the hero: "Product Manager who [ships.]" - words slide
// vertically on a spring-ish easing (same curve Perplexity uses).
const VERBS = ['ships.', 'scales.', 'automates.', 'launches.', 'experiments.'];

export function RotatingVerb() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(() => setI((p) => (p + 1) % VERBS.length), 2300);
    return () => clearInterval(t);
  }, [paused]);
  return (
    <span
      className="rot-verb"
      role="button"
      tabIndex={0}
      title="Click for the next one"
      aria-label={VERBS.join(' ')}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onClick={() => setI((p) => (p + 1) % VERBS.length)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setI((p) => (p + 1) % VERBS.length); } }}
    >
      <span className="rot-track" style={{ transform: `translateY(-${(i * 100) / VERBS.length}%)` }} aria-hidden="true">
        {VERBS.map((v) => (
          <span className="rot-word" key={v}>{v}</span>
        ))}
      </span>
    </span>
  );
}

// Click anywhere in the hero and a random PM word pops out where you clicked.
// Uses a shuffled "bag" so words don't repeat until the whole list was seen.
const PM_WORDS = [
  'Ship it.', 'North Star', 'MVP', 'Backlog zero', 'Pivot?', 'It depends.', 'P0', 'Roadmap',
  'OKRs', 'Dogfooding', 'Scope creep', 'RICE score', 'Kill the feature', 'Ask why 5x', 'Fail fast',
  'Retention > acquisition', 'Talk to users', 'Sunset it', 'Ruthless prioritisation', 'A/B it',
  'Vibes are not a metric', 'PRD or it didn\'t happen', 'Move the metric', 'Jobs to be done',
  'Stakeholders aligned', 'Launch day', 'Edge case', 'Quick win', 'Say no more', 'Ship, then learn',
  'Another sprint', 'Hallucination: <2%', 'Eval it first', 'Prompt, then PRD',
];
const HUES = [260, 320, 190, 150, 40];

export function HeroClickWords() {
  useEffect(() => {
    const hero = document.getElementById('hero');
    if (!hero) return undefined;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const layer = document.createElement('div');
    layer.className = 'click-words';
    layer.setAttribute('aria-hidden', 'true');
    document.body.appendChild(layer);

    let bag = [];
    let count = 0;
    const next = () => {
      if (bag.length === 0) bag = [...PM_WORDS].sort(() => Math.random() - 0.5);
      return bag.pop();
    };

    const onClick = (e) => {
      if (e.target.closest('a, button, input, textarea, [role="button"], .stat-item')) return;
      const x = e.clientX;
      const y = e.clientY;
      count += 1;

      const ring = document.createElement('span');
      ring.className = 'cw-ring';
      ring.style.left = `${x}px`;
      ring.style.top = `${y}px`;
      layer.appendChild(ring);
      setTimeout(() => ring.remove(), 900);
      if (reduce) return;

      const hue = HUES[count % HUES.length];
      const word = document.createElement('span');
      word.className = 'cw-word';
      word.textContent = next();
      word.style.left = `${Math.min(Math.max(x, 90), window.innerWidth - 90)}px`;
      word.style.top = `${y}px`;
      word.style.setProperty('--rot', `${(Math.random() * 14 - 7).toFixed(1)}deg`);
      word.style.setProperty('--hue', hue);
      layer.appendChild(word);
      while (layer.querySelectorAll('.cw-word').length > 10) layer.querySelector('.cw-word').remove();
      setTimeout(() => word.remove(), 2200);

      for (let i = 0; i < 7; i += 1) {
        const dot = document.createElement('i');
        dot.className = 'cw-dot';
        const a = (Math.PI * 2 * i) / 7 + Math.random();
        const d = 40 + Math.random() * 50;
        dot.style.left = `${x}px`;
        dot.style.top = `${y}px`;
        dot.style.setProperty('--dx', `${Math.cos(a) * d}px`);
        dot.style.setProperty('--dy', `${Math.sin(a) * d}px`);
        dot.style.setProperty('--hue', hue + i * 18);
        layer.appendChild(dot);
        setTimeout(() => dot.remove(), 800);
      }
    };

    hero.addEventListener('click', onClick);
    return () => {
      hero.removeEventListener('click', onClick);
      layer.remove();
    };
  }, []);
  return null;
}

// ---- Proof feed: real wins as a self-running "build log". Clicking a card
// asks Brij about it (DevarshAgent listens for the 'brij:ask' event).
const PROOF = [
  { tag: 'launched', title: 'Shipped a 0 to 1 AI MVP in 3 weeks', result: '3 weeks to MVP', co: 'Mindset Copilot', q: 'Tell me about the AI MVP Devarsh shipped in 3 weeks at Mindset Copilot' },
  { tag: 'managed', title: 'Owned payment ops behind $110M+ in volume', result: '$110M+ volume', co: 'Barclays', q: 'Tell me about managing $110M+ in payment volume at Barclays' },
  { tag: 'built', title: 'Built TutorWeights, an AI tutor eval tool', result: '47 to 95 score', co: 'Project', q: 'Tell me about TutorWeights, the AI tutor evaluation tool' },
  { tag: 'improved', title: 'Lifted clinician onboarding from 54% to 66%', result: '54% to 66%', co: 'Presence', q: 'Tell me about the onboarding completion improvement at Presence' },
  { tag: 'automated', title: 'Automated 200+ hours a month of reporting', result: '200+ hrs/mo', co: 'Presence', q: 'Tell me about the reporting automation at Presence that saved 200+ hours a month' },
  { tag: 'launched', title: 'Launched a career chatbot for 10K+ students', result: '92% NPS', co: 'UMBC', q: 'Tell me about the career support chatbot Devarsh launched at UMBC' },
  { tag: 'founded', title: 'Founded Muvely, won seed funding and a podium', result: '3rd at CBIC', co: 'Project', q: 'Tell me about Muvely, the 3D motion tracking app Devarsh founded' },
  { tag: 'shipped', title: 'Built AppliFirst, a live agentic SaaS', result: '20+ features', co: 'Project', q: 'Tell me about AppliFirst, the agentic SaaS product Devarsh built' },
];
const SLOTS = 4;

export function ProofFeed() {
  const [head, setHead] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(true);
  const rootRef = useRef(null);
  const reduce = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.2 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (paused || !inView || reduce) return undefined;
    const t = setInterval(() => { if (!document.hidden) setHead((h) => h + 1); }, 2800);
    return () => clearInterval(t);
  }, [paused, inView, reduce]);

  const ask = (q) => window.dispatchEvent(new CustomEvent('brij:ask', { detail: { question: q } }));

  const items = [];
  for (let n = head - 1; n < head + SLOTS; n += 1) {
    const slot = n - head;
    const d = PROOF[((n % PROOF.length) + PROOF.length) % PROOF.length];
    items.push({ n, slot, d, building: slot === SLOTS - 1 && n > SLOTS - 1 ? true : slot === SLOTS - 1 });
  }

  return (
    <section className="proof-fx" id="proof" ref={rootRef} aria-label="Selected wins">
      <div className="proof-copy">
        <span className="proof-eyebrow"><i className="pulse-dot" /> STILL SHIPPING</span>
        <h2 className="proof-title">Don't just read.<br /><em>Click one.</em></h2>
        <p className="proof-sub">Eight more things I built, launched or fixed, looping while you read. Pick a card and my AI twin Brij will tell you how it actually went down.</p>
        <span className="proof-hint">{paused ? 'Frozen. Now click one.' : 'Hover to freeze the feed'}</span>
      </div>
      <div
        className="proof-feed"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={() => setPaused(false)}
      >
        {items.map(({ n, slot, d, building }) => (
          <button
            key={n}
            type="button"
            className={`pf-card slot-${slot < 0 ? 'out' : slot} ${building ? 'is-building' : 'is-done'}`}
            style={{ '--slot': slot }}
            tabIndex={slot < 0 ? -1 : 0}
            aria-hidden={slot < 0}
            onClick={() => ask(d.q)}
          >
            <span className="pf-top">
              <span className="pf-num">{String((((n % PROOF.length) + PROOF.length) % PROOF.length) + 1).padStart(2, '0')}</span>
              <span className="pf-co">{d.co}</span>
              <span className="pf-ask">Ask Brij <b>↗</b></span>
            </span>
            <span className="pf-title">{d.title}</span>
            <span className="pf-bottom">
              <span className="pf-status">
                <span className="pf-building">BUILDING</span>
                <span className="pf-done">DONE <b>✓</b> {d.result}</span>
              </span>
              <span className="pf-bar"><i /></span>
              <span className="pf-tag">/{d.tag}</span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

// Big statement whose words light up as you scroll through it.
const MANIFESTO = [
  { t: "I'm a product manager who builds the prototype first." },
  { t: 'I ship', hl: true },
  { t: 'in weeks, let the data kill my favorite ideas, and talk to users before I write a single spec.' },
  { t: 'Ideas to launch.', hl: true },
  { t: 'Not decks.' },
];

export function Manifesto() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) { setProgress(1); return undefined; }
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const h = window.innerHeight;
      setProgress(Math.min(1, Math.max(0, (h * 0.85 - r.top) / (r.height + h * 0.3))));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  const words = MANIFESTO.flatMap((seg, si) =>
    seg.t.split(' ').map((w, wi) => ({ w, hl: seg.hl, key: `${si}-${wi}` }))
  );

  return (
    <section className="manifesto-fx" id="story" aria-label="About">
      <p ref={ref} className="manifesto-text">
        {words.map((x, idx) => (
          <span
            key={x.key}
            className={`mw ${x.hl ? 'hl' : ''} ${idx / words.length < progress ? 'on' : ''}`}
          >
            {x.w}{' '}
          </span>
        ))}
      </p>
    </section>
  );
}

// Sticky "stacked cards": each card sticks near the top while the next one
// slides over it; earlier cards scale down and dim as the next arrives.
// Disabled on small/short screens and for reduced motion (plain list there).
export function StackList({ children, className = '' }) {
  const ref = useRef(null);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const cards = Array.from(root.querySelectorAll(':scope > .stk-card'));
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const clamp = (v) => Math.min(1, Math.max(0, v));
    let raf = 0;

    const reset = () => cards.forEach((c) => { c.style.transform = ''; c.style.filter = ''; });

    const update = () => {
      raf = 0;
      const active = !reduce && window.innerWidth >= 900 && window.innerHeight >= 640;
      root.classList.toggle('stk-on', active);
      if (!active) { reset(); return; }
      const h = window.innerHeight;
      cards.forEach((c, i) => {
        const next = cards[i + 1];
        if (!next) { c.style.transform = ''; c.style.filter = ''; return; }
        const d = next.getBoundingClientRect().top - c.getBoundingClientRect().top;
        const k = clamp(1 - d / (h * 0.62));
        c.style.transform = `scale(${1 - k * 0.06})`;
        c.style.filter = `brightness(${1 - k * 0.35})`;
      });
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return <div ref={ref} className={`stk-list ${className}`}>{children}</div>;
}

// Page-level effects: scroll progress bar, header state, scroll reveals.
export default function ScrollFX() {
  const barRef = useRef(null);

  useEffect(() => {
    const nav = document.querySelector('.nav-glass');
    const portrait = document.querySelector('.hero-dark .profile-container');
    const name = document.querySelector('.hero-dark .hero-name');
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let raf = 0;

    // Line the top of the portrait up with the top of the big name, then let
    // it drift down with the scroll (and back up) for a soft parallax.
    const align = () => {
      if (!portrait || !name || window.innerWidth < 900) { if (portrait) portrait.style.marginTop = ''; return; }
      portrait.style.transform = 'none';
      const delta = name.getBoundingClientRect().top - portrait.getBoundingClientRect().top;
      portrait.style.marginTop = `${Math.max(0, delta)}px`;
    };

    const update = () => {
      raf = 0;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? window.scrollY / max : 0;
      if (barRef.current) barRef.current.style.transform = `scaleX(${p})`;
      if (nav) nav.classList.toggle('nav-scrolled', window.scrollY > 40);
      if (portrait && !reduce && window.innerWidth >= 900) {
        const shift = Math.min(window.scrollY * 0.45, window.innerHeight * 0.4);
        portrait.style.transform = `translateY(${shift}px)`;
      }
    };
    align();
    window.addEventListener('resize', align);
    const alignTimer = setTimeout(align, 400); // fonts/images settle
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', align);
      clearTimeout(alignTimer);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Fade/slide-up reveal for headers, cards and rows as they enter view.
  useEffect(() => {
    const targets = document.querySelectorAll(
      '.section-header, .hire-me-section .container > *'
    );
    targets.forEach((el) => el.setAttribute('data-reveal', ''));
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add('revealed'); io.unobserve(e.target); }
      }),
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    targets.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <div className="scroll-progress" ref={barRef} aria-hidden="true" />
      <HeroClickWords />
    </>
  );
}
